/**
 * The engine's content addresses — PRD 02 §4, ENGINEERING §3.
 *
 * Two keys, two cost profiles, and the difference is where the money is:
 *
 * - `variantInstanceId` keys the GENERATIVE half and excludes composition entirely, so
 *   every setting, prop, and backdrop resolves once and is shared across every author,
 *   story, and (later) cast. A `ready` hit is $0; a miss is one paid image.
 * - `sceneRenderId` keys the COMPOSITE half and includes the whole composition, so any
 *   change of character, variant, placement — or frame — yields a fresh render. Compositing
 *   is cheap per call, but it is *not* amortized across casts — that fan-out is the cost to
 *   watch, not the generation.
 */
import { hash } from './hash.js';
/**
 * Sort a variant's keys. Canonical serialization sorts anyway, so this does not change any
 * address; it exists so a variant is *stored* in one shape too. `{}` is the neutral variant
 * — the base reference.
 */
export function canonicalVariant(variant) {
    const out = {};
    for (const key of Object.keys(variant).sort()) {
        const value = variant[key];
        if (value !== undefined)
            out[key] = value;
    }
    return out;
}
/** `hash("variantInstance", primitiveId, primitiveVersion, variant, themeId)` and nothing else. */
export function variantInstanceId(primitiveId, primitiveVersion, variant, themeId) {
    return hash('variantInstance', primitiveId, primitiveVersion, canonicalVariant(variant), themeId);
}
/** The base frame every story beat is composed to: 4:3 (PRD 02 §13.1). */
export const BASE_FRAME = Object.freeze({ w: 4, h: 3 });
function gcd(a, b) {
    let x = Math.abs(a);
    let y = Math.abs(b);
    while (y !== 0) {
        const next = x % y;
        x = y;
        y = next;
    }
    return x;
}
/**
 * A frame ratio in its one canonical shape: reduced, positive integers.
 *
 * The rule that keeps the address honest: **absent and `{ w: 4, h: 3 }` hash identically**,
 * and so do `{ 8, 6 }`, `{ 1024, 768 }`, and any other way of writing the same aspect. A frame
 * that cannot be a ratio (zero, negative, non-finite, non-integer) falls back to the base
 * rather than poisoning the address with garbage.
 */
export function normalizeFrame(frame) {
    if (frame === undefined)
        return { ...BASE_FRAME };
    const { w, h } = frame;
    if (!Number.isInteger(w) ||
        !Number.isInteger(h) ||
        w <= 0 ||
        h <= 0 ||
        !Number.isFinite(w) ||
        !Number.isFinite(h)) {
        return { ...BASE_FRAME };
    }
    const divisor = gcd(w, h);
    return { w: w / divisor, h: h / divisor };
}
/** Whether a scene is composed to the base frame — i.e. whether `frame` says anything at all. */
export function isBaseFrame(frame) {
    const reduced = normalizeFrame(frame);
    return reduced.w === BASE_FRAME.w && reduced.h === BASE_FRAME.h;
}
/**
 * The composition signature of a scene: `setting`, `characters`, `props`, `beat`, `frame`.
 *
 * Excluded, because they do not change the picture: `line` (narration), `renderId` (the
 * cache pointer itself), and `place` (an author's note about where a character stands —
 * only what reaches the compositor is hashed, and that is `transform`). Included:
 * `transform`, because moving a piece changes the picture, and `frame`, because a different
 * crop is a different picture (a re-frame is a new composition, not a transform of the old).
 *
 * `props` absent and `props: []` normalize to the same address; so do `frame` absent and the
 * base 4:3.
 */
export function sceneRenderId(scene, themeId) {
    const composition = {
        setting: {
            primitiveId: scene.setting.primitiveId,
            version: scene.setting.version,
            variant: canonicalVariant(scene.setting.variant),
            transform: scene.setting.transform,
        },
        characters: scene.characters.map((character) => ({
            primitiveId: character.primitiveId,
            version: character.version,
            variant: canonicalVariant(character.variant),
            transform: character.transform,
        })),
        props: (scene.props ?? []).map((prop) => ({
            primitiveId: prop.primitiveId,
            version: prop.version,
            variant: canonicalVariant(prop.variant),
            transform: prop.transform,
        })),
        beat: scene.beat
            ? { primitiveId: scene.beat.primitiveId, version: scene.beat.version }
            : undefined,
        frame: normalizeFrame(scene.frame),
    };
    return hash('sceneRender', composition, themeId);
}
/**
 * Composes already-cached scene renders. **The adaptation is the address**, not the story:
 * a medium lays out an adaptation's effective scenes, and two adaptations of one story into
 * one medium are two different objects. `orderedSceneRenderIds` is order-significant (it is
 * the page order); `spec` folds a film's motion into the address, so studio, gate, and
 * renderer all compute the same id from the adaptation alone.
 */
export function mediumRenderId(adaptationId, mediumId, themeId, orderedSceneRenderIds, spec) {
    return hash('mediumRender', adaptationId, mediumId, themeId, orderedSceneRenderIds, spec ?? null);
}
//# sourceMappingURL=address.js.map